import { useEffect, useState } from "react";

const DISMISS_KEY = "stAcademy_installPromptDismissed";
const COOKIE_KEY = "stAcademy_cookieConsent";
const DISMISS_DAYS = 14;

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true);

const isIos = () => typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);

// Cross-browser install nudge: Chrome/Edge/Android fire `beforeinstallprompt`, which this
// captures and replays from our own button (browsers hide the native mini-infobar unless you
// call prompt() yourself). iOS Safari never fires that event -- there's no programmatic install
// there -- so it gets the documented manual path instead ("Share -> Add to Home Screen").
const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || isStandalone()) return;

    const dismissedAt = window.localStorage.getItem(DISMISS_KEY);
    if (dismissedAt && Date.now() - Number(dismissedAt) < DISMISS_DAYS * 24 * 60 * 60 * 1000) return;

    // Don't stack on top of the cookie banner -- wait for that decision first. The consent
    // choice happens in this same tab/localStorage write, which doesn't fire a "storage"
    // event locally, so this polls instead of listening for one.
    const reveal = () => {
      if (window.localStorage.getItem(COOKIE_KEY)) {
        setVisible(true);
        return;
      }
      const interval = setInterval(() => {
        if (window.localStorage.getItem(COOKIE_KEY)) {
          setVisible(true);
          clearInterval(interval);
        }
      }, 1000);
    };

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(reveal, 4000);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);

    if (isIos()) {
      setShowIosHint(true);
      setTimeout(reveal, 4000);
    }

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
  }, []);

  const dismiss = () => {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    dismiss();
  };

  if (!visible || (!deferredPrompt && !showIosHint)) return null;

  return (
    <div className="rbt-install-prompt" role="dialog" aria-live="polite" aria-label="Install SmartTech Academy">
      <button type="button" className="rbt-install-prompt__close" aria-label="Dismiss" onClick={dismiss}>
        <i className="feather-x"></i>
      </button>

      <div className="rbt-install-prompt__top">
        <span className="rbt-install-prompt__icon">
          <i className="feather-download"></i>
        </span>
        <div className="rbt-install-prompt__body">
          <strong>Install SmartTech Academy</strong>
          {showIosHint && !deferredPrompt ? (
            <p>
              Tap the <i className="feather-share"></i> Share icon, then &quot;Add to Home
              Screen&quot; for quick, full-screen access &mdash; no app store needed.
            </p>
          ) : (
            <p>Add it to your home screen for one-tap access and offline course browsing, no app store needed.</p>
          )}
        </div>
      </div>

      {deferredPrompt && (
        <div className="rbt-install-prompt__actions">
          <button type="button" className="rbt-btn btn-gradient w-100" onClick={install}>
            <span className="btn-text">Install app</span>
          </button>
          <button type="button" className="rbt-install-prompt__later" onClick={dismiss}>
            Not now
          </button>
        </div>
      )}
    </div>
  );
};

export default InstallPrompt;
